/**
 * Copyright (c) 2026 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * Code-generated by `src/cli/syminfo` from `data/sym/syminfo.lib` - do not
 * edit manually. Regenerate with `npm run gen-syminfo`.
 */

/**
 * `[itaNumber, ccp4Number, names, hall, basisop]` tuples for every
 * `syminfo.lib` setting (540 total - all CCP4-numbered
 * settings AND non-numbered axis/cell/origin alternatives). See
 * `../../../cli/syminfo` for how this is generated, and `./tables`'s
 * `SpacegroupEntry`/`SpacegroupData` for the friendlier object-shaped
 * form consumed by the rest of the codebase.
 */
export type RawSpacegroupEntry = readonly [itaNumber: number, ccp4Number: number, names: readonly string[], hall: string, basisop: string];

export const RawSpacegroupData: ReadonlyArray<RawSpacegroupEntry> = [
    [1, 1, ['P 1'], 'P 1', 'x,y,z'],
    [2, 2, ['P -1'], '-P 1', 'x,y,z'],
    [3, 3, ['P 1 2 1'], 'P 2y', 'x,y,z'],
    [3, 1003, ['P 1 1 2'], 'P 2', 'z,x,y'],
    [3, 0, ['P 2 1 1'], 'P 2y (y,z,x)', 'y,z,x'],
    [4, 4, ['P 1 21 1'], 'P 2yb', 'x,y,z'],
    [4, 1004, ['P 1 1 21'], 'P 2c', 'z,x,y'],
    [4, 0, ['P 21 1 1'], 'P 2yb (y,z,x)', 'y,z,x'],
    [5, 5, ['C 1 2 1'], 'C 2y', 'x,y,z'],
    [5, 2005, ['A 1 2 1', 'A 2'], 'A 2y', 'z,y,-x'],
    [5, 4005, ['I 2', 'I 1 2 1'], 'I 2y', 'x,y,-x+z'],
    [5, 0, ['A 1 1 2'], 'C 2y (z,x,y)', 'z,x,y'],
    [5, 1005, ['B 2', 'B 1 1 2'], 'B 2', '-x,z,y'],
    [5, 0, ['I 1 1 2'], 'C 2y (-x+z,x,y)', '-x+z,x,y'],
    [5, 0, ['B 2 1 1'], 'C 2y (y,z,x)', 'y,z,x'],
    [5, 0, ['C 2 1 1'], 'C 2y (y,-x,z)', 'y,-x,z'],
    [5, 0, ['I 2 1 1'], 'C 2y (y,-x+z,x)', 'y,-x+z,x'],
    [6, 6, ['P 1 m 1'], 'P -2y', 'x,y,z'],
    [6, 1006, ['P 1 1 m'], 'P -2', 'z,x,y'],
    [6, 0, ['P m 1 1'], 'P -2y (y,z,x)', 'y,z,x'],
    [7, 7, ['P 1 c 1'], 'P -2yc', 'x,y,z'],
    [7, 0, ['P 1 n 1'], 'P -2yc (x-z,y,z)', 'x-z,y,z'],
    [7, 0, ['P 1 a 1'], 'P -2yc (z,y,-x)', 'z,y,-x'],
    [7, 0, ['P 1 1 a'], 'P -2yc (z,x,y)', 'z,x,y'],
    [7, 0, ['P 1 1 n'], 'P -2yc (z,x-z,y)', 'z,x-z,y'],
    [7, 1007, ['P 1 1 b'], 'P -2b', '-x,z,y'],
    [7, 0, ['P b 1 1'], 'P -2yc (y,z,x)', 'y,z,x'],
    [7, 0, ['P n 1 1'], 'P -2yc (y,z,x-z)', 'y,z,x-z'],
    [7, 0, ['P c 1 1'], 'P -2yc (y,-x,z)', 'y,-x,z'],
    [8, 8, ['C 1 m 1'], 'C -2y', 'x,y,z'],
    [8, 0, ['A 1 m 1'], 'C -2y (z,y,-x)', 'z,y,-x'],
    [8, 0, ['I 1 m 1'], 'C -2y (x,y,-x+z)', 'x,y,-x+z'],
    [8, 0, ['A 1 1 m'], 'C -2y (z,x,y)', 'z,x,y'],
    [8, 0, ['B 1 1 m'], 'C -2y (-x,z,y)', '-x,z,y'],
    [8, 0, ['I 1 1 m'], 'C -2y (-x+z,x,y)', '-x+z,x,y'],
    [8, 0, ['B m 1 1'], 'C -2y (y,z,x)', 'y,z,x'],
    [8, 0, ['C m 1 1'], 'C -2y (y,-x,z)', 'y,-x,z'],
    [8, 0, ['I m 1 1'], 'C -2y (y,-x+z,x)', 'y,-x+z,x'],
    [9, 9, ['C 1 c 1'], 'C -2yc', 'x,y,z'],
    [9, 0, ['A 1 n 1'], 'C -2yc (z,y,-x+z)', 'z,y,-x+z'],
    [9, 0, ['I 1 a 1'], 'C -2yc (x+z,y,-x)', 'x+z,y,-x'],
    [9, 0, ['A 1 a 1'], 'C -2yc (z,y,-x)', 'z,y,-x'],
    [9, 0, ['C 1 n 1'], 'C -2yc (x+1/4,y+1/4,z)', 'x+1/4,y+1/4,z'],
    [9, 0, ['I 1 c 1'], 'C -2yc (x,y,-x+z)', 'x,y,-x+z'],
    [9, 0, ['A 1 1 a'], 'C -2yc (z,x,y)', 'z,x,y'],
    [9, 0, ['B 1 1 n'], 'C -2yc (-x+z,z,y)', '-x+z,z,y'],
    [9, 0, ['I 1 1 b'], 'C -2yc (-x,x+z,y)', '-x,x+z,y'],
    [9, 1009, ['B 1 1 b'], 'B -2b', '-x,z,y'],
    [9, 0, ['A 1 1 n'], 'C -2yc (z,x-z,y)', 'z,x-z,y'],
    [9, 0, ['I 1 1 a'], 'C -2yc (-x+z,x,y)', '-x+z,x,y'],
    [9, 0, ['B b 1 1'], 'C -2yc (y,z,x)', 'y,z,x'],
    [9, 0, ['C n 1 1'], 'C -2yc (y,-x+z,z)', 'y,-x+z,z'],
    [9, 0, ['I c 1 1'], 'C -2yc (y,-x,x+z)', 'y,-x,x+z'],
    [9, 0, ['C c 1 1'], 'C -2yc (y,-x,z)', 'y,-x,z'],
    [9, 0, ['B n 1 1'], 'C -2yc (y,z,x-z)', 'y,z,x-z'],
    [9, 0, ['I b 1 1'], 'C -2yc (y,-x+z,x)', 'y,-x+z,x'],
    [10, 10, ['P 1 2/m 1'], '-P 2y', 'x,y,z'],
    [10, 1010, ['P 1 1 2/m'], '-P 2', 'z,x,y'],
    [10, 0, ['P 2/m 1 1'], '-P 2y (y,z,x)', 'y,z,x'],
    [11, 11, ['P 1 21/m 1'], '-P 2yb', 'x,y,z'],
    [11, 1011, ['P 1 1 21/m'], '-P 2c', 'z,x,y'],
    [11, 0, ['P 21/m 1 1'], '-P 2yb (y,z,x)', 'y,z,x'],
    [12, 12, ['C 1 2/m 1'], '-C 2y', 'x,y,z'],
    [12, 0, ['A 1 2/m 1'], '-C 2y (z,y,-x)', 'z,y,-x'],
    [12, 0, ['I 1 2/m 1'], '-C 2y (x,y,-x+z)', 'x,y,-x+z'],
    [12, 0, ['A 1 1 2/m'], '-C 2y (z,x,y)', 'z,x,y'],
    [12, 1012, ['B 1 1 2/m'], '-B 2', '-x,z,y'],
    [12, 0, ['I 1 1 2/m'], '-C 2y (-x+z,x,y)', '-x+z,x,y'],
    [12, 0, ['B 2/m 1 1'], '-C 2y (y,z,x)', 'y,z,x'],
    [12, 0, ['C 2/m 1 1'], '-C 2y (y,-x,z)', 'y,-x,z'],
    [12, 0, ['I 2/m 1 1'], '-C 2y (y,-x+z,x)', 'y,-x+z,x'],
    [13, 13, ['P 1 2/c 1'], '-P 2yc', 'x,y,z'],
    [13, 0, ['P 1 2/n 1'], '-P 2yc (x-z,y,z)', 'x-z,y,z'],
    [13, 0, ['P 1 2/a 1'], '-P 2yc (z,y,-x)', 'z,y,-x'],
    [13, 0, ['P 1 1 2/a'], '-P 2yc (z,x,y)', 'z,x,y'],
    [13, 0, ['P 1 1 2/n'], '-P 2yc (z,x-z,y)', 'z,x-z,y'],
    [13, 1013, ['P 1 1 2/b'], '-P 2b', '-x,z,y'],
    [13, 0, ['P 2/b 1 1'], '-P 2yc (y,z,x)', 'y,z,x'],
    [13, 0, ['P 2/n 1 1'], '-P 2yc (y,z,x-z)', 'y,z,x-z'],
    [13, 0, ['P 2/c 1 1'], '-P 2yc (y,-x,z)', 'y,-x,z'],
    [14, 14, ['P 1 21/c 1'], '-P 2ybc', 'x,y,z'],
    [14, 2014, ['P 1 21/n 1'], '-P 2yn', 'x-z,y,z'],
    [14, 3014, ['P 1 21/a 1'], '-P 2yab', 'z,y,-x'],
    [14, 0, ['P 1 1 21/a'], '-P 2ybc (z,x,y)', 'z,x,y'],
    [14, 0, ['P 1 1 21/n'], '-P 2ybc (z,x-z,y)', 'z,x-z,y'],
    [14, 1014, ['P 1 1 21/b'], '-P 2bc', '-x,z,y'],
    [14, 0, ['P 21/b 1 1'], '-P 2ybc (y,z,x)', 'y,z,x'],
    [14, 0, ['P 21/n 1 1'], '-P 2ybc (y,z,x-z)', 'y,z,x-z'],
    [14, 0, ['P 21/c 1 1'], '-P 2ybc (y,-x,z)', 'y,-x,z'],
    [15, 15, ['C 1 2/c 1'], '-C 2yc', 'x,y,z'],
    [15, 0, ['A 1 2/n 1'], '-C 2yc (z,y,-x+z)', 'z,y,-x+z'],
    [15, 0, ['I 1 2/a 1'], '-C 2yc (x+z,y,-x)', 'x+z,y,-x'],
    [15, 0, ['A 1 2/a 1'], '-C 2yc (z,y,-x)', 'z,y,-x'],
    [15, 0, ['C 1 2/n 1'], '-C 2yc (x+1/4,y-1/4,z)', 'x+1/4,y-1/4,z'],
    [15, 0, ['I 1 2/c 1'], '-C 2yc (x,y,-x+z)', 'x,y,-x+z'],
    [15, 0, ['A 1 1 2/a'], '-C 2yc (z,x,y)', 'z,x,y'],
    [15, 0, ['B 1 1 2/n'], '-C 2yc (-x+z,z,y)', '-x+z,z,y'],
    [15, 0, ['I 1 1 2/b'], '-C 2yc (-x,x+z,y)', '-x,x+z,y'],
    [15, 1015, ['B 1 1 2/b'], '-B 2b', '-x,z,y'],
    [15, 0, ['A 1 1 2/n'], '-C 2yc (z,x-z,y)', 'z,x-z,y'],
    [15, 0, ['I 1 1 2/a'], '-C 2yc (-x+z,x,y)', '-x+z,x,y'],
    [15, 0, ['B 2/b 1 1'], '-C 2yc (y,z,x)', 'y,z,x'],
    [15, 0, ['C 2/n 1 1'], '-C 2yc (y,-x+z,z)', 'y,-x+z,z'],
    [15, 0, ['I 2/c 1 1'], '-C 2yc (y,-x,x+z)', 'y,-x,x+z'],
    [15, 0, ['C 2/c 1 1'], '-C 2yc (y,-x,z)', 'y,-x,z'],
    [15, 0, ['B 2/n 1 1'], '-C 2yc (y,z,x-z)', 'y,z,x-z'],
    [15, 0, ['I 2/b 1 1'], '-C 2yc (y,-x+z,x)', 'y,-x+z,x'],
    [16, 16, ['P 2 2 2'], 'P 2 2', 'x,y,z'],
    [17, 17, ['P 2 2 21'], 'P 2c 2', 'x,y,z'],
    [17, 1017, ['P 21 2 2'], 'P 2a 2a', 'z,x,y'],
    [17, 2017, ['P 2 21 2'], 'P 2 2b', 'y,z,x'],
    [18, 18, ['P 21 21 2'], 'P 2 2ab', 'x,y,z'],
    [18, 3018, ['P 2 21 21'], 'P 2bc 2', 'z,x,y'],
    [18, 2018, ['P 21 2 21'], 'P 2ac 2ac', 'y,z,x'],
    [19, 19, ['P 21 21 21'], 'P 2ac 2ab', 'x,y,z'],
    [20, 20, ['C 2 2 21'], 'C 2c 2', 'x,y,z'],
    [20, 0, ['A 21 2 2'], 'C 2c 2 (z,x,y)', 'z,x,y'],
    [20, 0, ['B 2 21 2'], 'C 2c 2 (y,z,x)', 'y,z,x'],
    [21, 21, ['C 2 2 2'], 'C 2 2', 'x,y,z'],
    [21, 0, ['A 2 2 2'], 'C 2 2 (z,x,y)', 'z,x,y'],
    [21, 0, ['B 2 2 2'], 'C 2 2 (y,z,x)', 'y,z,x'],
    [22, 22, ['F 2 2 2'], 'F 2 2', 'x,y,z'],
    [23, 23, ['I 2 2 2'], 'I 2 2', 'x,y,z'],
    [24, 24, ['I 21 21 21'], 'I 2b 2c', 'x,y,z'],
    [25, 25, ['P m m 2'], 'P 2 -2', 'x,y,z'],
    [25, 0, ['P 2 m m'], 'P 2 -2 (z,x,y)', 'z,x,y'],
    [25, 0, ['P m 2 m'], 'P 2 -2 (y,z,x)', 'y,z,x'],
    [26, 26, ['P m c 21'], 'P 2c -2', 'x,y,z'],
    [26, 0, ['P c m 21'], 'P 2c -2 (y,-x,z)', 'y,-x,z'],
    [26, 0, ['P 21 m a'], 'P 2c -2 (z,x,y)', 'z,x,y'],
    [26, 0, ['P 21 a m'], 'P 2c -2 (z,y,-x)', 'z,y,-x'],
    [26, 0, ['P b 21 m'], 'P 2c -2 (y,z,x)', 'y,z,x'],
    [26, 0, ['P m 21 b'], 'P 2c -2 (-x,z,y)', '-x,z,y'],
    [27, 27, ['P c c 2'], 'P 2 -2c', 'x,y,z'],
    [27, 0, ['P 2 a a'], 'P 2 -2c (z,x,y)', 'z,x,y'],
    [27, 0, ['P b 2 b'], 'P 2 -2c (y,z,x)', 'y,z,x'],
    [28, 28, ['P m a 2'], 'P 2 -2a', 'x,y,z'],
    [28, 0, ['P b m 2'], 'P 2 -2a (y,-x,z)', 'y,-x,z'],
    [28, 0, ['P 2 m b'], 'P 2 -2a (z,x,y)', 'z,x,y'],
    [28, 0, ['P 2 c m'], 'P 2 -2a (z,y,-x)', 'z,y,-x'],
    [28, 0, ['P c 2 m'], 'P 2 -2a (y,z,x)', 'y,z,x'],
    [28, 0, ['P m 2 a'], 'P 2 -2a (-x,z,y)', '-x,z,y'],
    [29, 29, ['P c a 21'], 'P 2c -2ac', 'x,y,z'],
    [29, 0, ['P b c 21'], 'P 2c -2ac (y,-x,z)', 'y,-x,z'],
    [29, 0, ['P 21 a b'], 'P 2c -2ac (z,x,y)', 'z,x,y'],
    [29, 0, ['P 21 c a'], 'P 2c -2ac (z,y,-x)', 'z,y,-x'],
    [29, 0, ['P c 21 b'], 'P 2c -2ac (y,z,x)', 'y,z,x'],
    [29, 0, ['P b 21 a'], 'P 2c -2ac (-x,z,y)', '-x,z,y'],
    [30, 30, ['P n c 2'], 'P 2 -2bc', 'x,y,z'],
    [30, 0, ['P c n 2'], 'P 2 -2bc (y,-x,z)', 'y,-x,z'],
    [30, 0, ['P 2 n a'], 'P 2 -2bc (z,x,y)', 'z,x,y'],
    [30, 0, ['P 2 a n'], 'P 2 -2bc (z,y,-x)', 'z,y,-x'],
    [30, 0, ['P b 2 n'], 'P 2 -2bc (y,z,x)', 'y,z,x'],
    [30, 0, ['P n 2 b'], 'P 2 -2bc (-x,z,y)', '-x,z,y'],
    [31, 31, ['P m n 21'], 'P 2ac -2', 'x,y,z'],
    [31, 0, ['P n m 21'], 'P 2ac -2 (y,-x,z)', 'y,-x,z'],
    [31, 0, ['P 21 m n'], 'P 2ac -2 (z,x,y)', 'z,x,y'],
    [31, 0, ['P 21 n m'], 'P 2ac -2 (z,y,-x)', 'z,y,-x'],
    [31, 0, ['P n 21 m'], 'P 2ac -2 (y,z,x)', 'y,z,x'],
    [31, 0, ['P m 21 n'], 'P 2ac -2 (-x,z,y)', '-x,z,y'],
    [32, 32, ['P b a 2'], 'P 2 -2ab', 'x,y,z'],
    [32, 0, ['P 2 c b'], 'P 2 -2ab (z,x,y)', 'z,x,y'],
    [32, 0, ['P c 2 a'], 'P 2 -2ab (y,z,x)', 'y,z,x'],
    [33, 33, ['P n a 21'], 'P 2c -2n', 'x,y,z'],
    [33, 0, ['P b n 21'], 'P 2c -2n (y,-x,z)', 'y,-x,z'],
    [33, 0, ['P 21 n b'], 'P 2c -2n (z,x,y)', 'z,x,y'],
    [33, 0, ['P 21 c n'], 'P 2c -2n (z,y,-x)', 'z,y,-x'],
    [33, 0, ['P c 21 n'], 'P 2c -2n (y,z,x)', 'y,z,x'],
    [33, 0, ['P n 21 a'], 'P 2c -2n (-x,z,y)', '-x,z,y'],
    [34, 34, ['P n n 2'], 'P 2 -2n', 'x,y,z'],
    [34, 0, ['P 2 n n'], 'P 2 -2n (z,x,y)', 'z,x,y'],
    [34, 0, ['P n 2 n'], 'P 2 -2n (y,z,x)', 'y,z,x'],
    [35, 35, ['C m m 2'], 'C 2 -2', 'x,y,z'],
    [35, 0, ['A 2 m m'], 'C 2 -2 (z,x,y)', 'z,x,y'],
    [35, 0, ['B m 2 m'], 'C 2 -2 (y,z,x)', 'y,z,x'],
    [36, 36, ['C m c 21'], 'C 2c -2', 'x,y,z'],
    [36, 0, ['C c m 21'], 'C 2c -2 (y,-x,z)', 'y,-x,z'],
    [36, 0, ['A 21 m a'], 'C 2c -2 (z,x,y)', 'z,x,y'],
    [36, 0, ['A 21 a m'], 'C 2c -2 (z,y,-x)', 'z,y,-x'],
    [36, 0, ['B b 21 m'], 'C 2c -2 (y,z,x)', 'y,z,x'],
    [36, 0, ['B m 21 b'], 'C 2c -2 (-x,z,y)', '-x,z,y'],
    [37, 37, ['C c c 2'], 'C 2 -2c', 'x,y,z'],
    [37, 0, ['A 2 a a'], 'C 2 -2c (z,x,y)', 'z,x,y'],
    [37, 0, ['B b 2 b'], 'C 2 -2c (y,z,x)', 'y,z,x'],
    [38, 38, ['A m m 2'], 'A 2 -2', 'x,y,z'],
    [38, 0, ['B m m 2'], 'A 2 -2 (y,-x,z)', 'y,-x,z'],
    [38, 0, ['B 2 m m'], 'A 2 -2 (z,x,y)', 'z,x,y'],
    [38, 0, ['C 2 m m'], 'A 2 -2 (z,y,-x)', 'z,y,-x'],
    [38, 0, ['C m 2 m'], 'A 2 -2 (y,z,x)', 'y,z,x'],
    [38, 0, ['A m 2 m'], 'A 2 -2 (-x,z,y)', '-x,z,y'],
    [39, 39, ['A b m 2'], 'A 2 -2b', 'x,y,z'],
    [39, 0, ['B m a 2'], 'A 2 -2b (y,-x,z)', 'y,-x,z'],
    [39, 0, ['B 2 c m'], 'A 2 -2b (z,x,y)', 'z,x,y'],
    [39, 0, ['C 2 m b'], 'A 2 -2b (z,y,-x)', 'z,y,-x'],
    [39, 0, ['C m 2 a'], 'A 2 -2b (y,z,x)', 'y,z,x'],
    [39, 0, ['A c 2 m'], 'A 2 -2b (-x,z,y)', '-x,z,y'],
    [40, 40, ['A m a 2'], 'A 2 -2a', 'x,y,z'],
    [40, 0, ['B b m 2'], 'A 2 -2a (y,-x,z)', 'y,-x,z'],
    [40, 0, ['B 2 m b'], 'A 2 -2a (z,x,y)', 'z,x,y'],
    [40, 0, ['C 2 c m'], 'A 2 -2a (z,y,-x)', 'z,y,-x'],
    [40, 0, ['C c 2 m'], 'A 2 -2a (y,z,x)', 'y,z,x'],
    [40, 0, ['A m 2 a'], 'A 2 -2a (-x,z,y)', '-x,z,y'],
    [41, 41, ['A b a 2'], 'A 2 -2ab', 'x,y,z'],
    [41, 0, ['B b a 2'], 'A 2 -2ab (y,-x,z)', 'y,-x,z'],
    [41, 0, ['B 2 c b'], 'A 2 -2ab (z,x,y)', 'z,x,y'],
    [41, 0, ['C 2 c b'], 'A 2 -2ab (z,y,-x)', 'z,y,-x'],
    [41, 0, ['C c 2 a'], 'A 2 -2ab (y,z,x)', 'y,z,x'],
    [41, 0, ['A c 2 a'], 'A 2 -2ab (-x,z,y)', '-x,z,y'],
    [42, 42, ['F m m 2'], 'F 2 -2', 'x,y,z'],
    [42, 0, ['F 2 m m'], 'F 2 -2 (z,x,y)', 'z,x,y'],
    [42, 0, ['F m 2 m'], 'F 2 -2 (y,z,x)', 'y,z,x'],
    [43, 43, ['F d d 2'], 'F 2 -2d', 'x,y,z'],
    [43, 0, ['F 2 d d'], 'F 2 -2d (z,x,y)', 'z,x,y'],
    [43, 0, ['F d 2 d'], 'F 2 -2d (y,z,x)', 'y,z,x'],
    [44, 44, ['I m m 2'], 'I 2 -2', 'x,y,z'],
    [44, 0, ['I 2 m m'], 'I 2 -2 (z,x,y)', 'z,x,y'],
    [44, 0, ['I m 2 m'], 'I 2 -2 (y,z,x)', 'y,z,x'],
    [45, 45, ['I b a 2'], 'I 2 -2c', 'x,y,z'],
    [45, 0, ['I 2 c b'], 'I 2 -2c (z,x,y)', 'z,x,y'],
    [45, 0, ['I c 2 a'], 'I 2 -2c (y,z,x)', 'y,z,x'],
    [46, 46, ['I m a 2'], 'I 2 -2a', 'x,y,z'],
    [46, 0, ['I b m 2'], 'I 2 -2a (y,-x,z)', 'y,-x,z'],
    [46, 0, ['I 2 m b'], 'I 2 -2a (z,x,y)', 'z,x,y'],
    [46, 0, ['I 2 c m'], 'I 2 -2a (z,y,-x)', 'z,y,-x'],
    [46, 0, ['I c 2 m'], 'I 2 -2a (y,z,x)', 'y,z,x'],
    [46, 0, ['I m 2 a'], 'I 2 -2a (-x,z,y)', '-x,z,y'],
    [47, 47, ['P m m m', 'P 2/m 2/m 2/m'], '-P 2 2', 'x,y,z'],
    [48, 48, ['P n n n', 'P 2/n 2/n 2/n'], 'P 2 2 -1n', 'x-1/4,y-1/4,z-1/4'],
    [48, 0, ['P n n n'], '-P 2ab 2bc', 'x,y,z'],
    [49, 49, ['P c c m', 'P 2/c 2/c 2/m'], '-P 2 2c', 'x,y,z'],
    [49, 0, ['P m a a'], '-P 2 2c (z,x,y)', 'z,x,y'],
    [49, 0, ['P b m b'], '-P 2 2c (y,z,x)', 'y,z,x'],
    [50, 50, ['P b a n', 'P 2/b 2/a 2/n'], 'P 2 2 -1ab', 'x-1/4,y-1/4,z'],
    [50, 0, ['P b a n'], '-P 2ab 2b', 'x,y,z'],
    [50, 0, ['P n c b'], '-P 2ab 2b (z,x-1/4,y-1/4)', 'z,x-1/4,y-1/4'],
    [50, 0, ['P n c b'], '-P 2ab 2b (z,x,y)', 'z,x,y'],
    [50, 0, ['P c n a'], '-P 2ab 2b (y-1/4,z,x-1/4)', 'y-1/4,z,x-1/4'],
    [50, 0, ['P c n a'], '-P 2ab 2b (y,z,x)', 'y,z,x'],
    [51, 51, ['P m m a', 'P 21/m 2/m 2/a'], '-P 2a 2a', 'x,y,z'],
    [51, 0, ['P m m b'], '-P 2a 2a (y,-x,z)', 'y,-x,z'],
    [51, 0, ['P b m m'], '-P 2a 2a (z,x,y)', 'z,x,y'],
    [51, 0, ['P c m m'], '-P 2a 2a (z,y,-x)', 'z,y,-x'],
    [51, 0, ['P m c m'], '-P 2a 2a (y,z,x)', 'y,z,x'],
    [51, 0, ['P m a m'], '-P 2a 2a (-x,z,y)', '-x,z,y'],
    [52, 52, ['P n n a', 'P 2/n 21/n 2/a'], '-P 2a 2bc', 'x,y,z'],
    [52, 0, ['P n n b'], '-P 2a 2bc (y,-x,z)', 'y,-x,z'],
    [52, 0, ['P b n n'], '-P 2a 2bc (z,x,y)', 'z,x,y'],
    [52, 0, ['P c n n'], '-P 2a 2bc (z,y,-x)', 'z,y,-x'],
    [52, 0, ['P n c n'], '-P 2a 2bc (y,z,x)', 'y,z,x'],
    [52, 0, ['P n a n'], '-P 2a 2bc (-x,z,y)', '-x,z,y'],
    [53, 53, ['P m n a', 'P 2/m 2/n 21/a'], '-P 2ac 2', 'x,y,z'],
    [53, 0, ['P n m b'], '-P 2ac 2 (y,-x,z)', 'y,-x,z'],
    [53, 0, ['P b m n'], '-P 2ac 2 (z,x,y)', 'z,x,y'],
    [53, 0, ['P c n m'], '-P 2ac 2 (z,y,-x)', 'z,y,-x'],
    [53, 0, ['P n c m'], '-P 2ac 2 (y,z,x)', 'y,z,x'],
    [53, 0, ['P m a n'], '-P 2ac 2 (-x,z,y)', '-x,z,y'],
    [54, 54, ['P c c a', 'P 21/c 2/c 2/a'], '-P 2a 2ac', 'x,y,z'],
    [54, 0, ['P c c b'], '-P 2a 2ac (y,-x,z)', 'y,-x,z'],
    [54, 0, ['P b a a'], '-P 2a 2ac (z,x,y)', 'z,x,y'],
    [54, 0, ['P c a a'], '-P 2a 2ac (z,y,-x)', 'z,y,-x'],
    [54, 0, ['P b c b'], '-P 2a 2ac (y,z,x)', 'y,z,x'],
    [54, 0, ['P b a b'], '-P 2a 2ac (-x,z,y)', '-x,z,y'],
    [55, 55, ['P b a m', 'P 21/b 21/a 2/m'], '-P 2 2ab', 'x,y,z'],
    [55, 0, ['P m c b'], '-P 2 2ab (z,x,y)', 'z,x,y'],
    [55, 0, ['P c m a'], '-P 2 2ab (y,z,x)', 'y,z,x'],
    [56, 56, ['P c c n', 'P 21/c 21/c 2/n'], '-P 2ab 2ac', 'x,y,z'],
    [56, 0, ['P n a a'], '-P 2ab 2ac (z,x,y)', 'z,x,y'],
    [56, 0, ['P b n b'], '-P 2ab 2ac (y,z,x)', 'y,z,x'],
    [57, 57, ['P b c m', 'P 2/b 21/c 21/m'], '-P 2c 2b', 'x,y,z'],
    [57, 0, ['P c a m'], '-P 2c 2b (y,-x,z)', 'y,-x,z'],
    [57, 0, ['P m c a'], '-P 2c 2b (z,x,y)', 'z,x,y'],
    [57, 0, ['P m a b'], '-P 2c 2b (z,y,-x)', 'z,y,-x'],
    [57, 0, ['P b m a'], '-P 2c 2b (y,z,x)', 'y,z,x'],
    [57, 0, ['P c m b'], '-P 2c 2b (-x,z,y)', '-x,z,y'],
    [58, 58, ['P n n m', 'P 21/n 21/n 2/m'], '-P 2 2n', 'x,y,z'],
    [58, 0, ['P m n n'], '-P 2 2n (z,x,y)', 'z,x,y'],
    [58, 0, ['P n m n'], '-P 2 2n (y,z,x)', 'y,z,x'],
    [59, 59, ['P m m n', 'P 21/m 21/m 2/n'], 'P 2 2ab -1ab', 'x-1/4,y-1/4,z'],
    [59, 1059, ['P 21/m 21/m 2/n a'], '-P 2ab 2a', 'x,y,z'],
    [59, 0, ['P n m m'], '-P 2ab 2a (z,x-1/4,y-1/4)', 'z,x-1/4,y-1/4'],
    [59, 0, ['P n m m'], '-P 2ab 2a (z,x,y)', 'z,x,y'],
    [59, 0, ['P m n m'], '-P 2ab 2a (y-1/4,z,x-1/4)', 'y-1/4,z,x-1/4'],
    [59, 0, ['P m n m'], '-P 2ab 2a (y,z,x)', 'y,z,x'],
    [60, 60, ['P b c n', 'P 21/b 2/c 21/n'], '-P 2n 2ab', 'x,y,z'],
    [60, 0, ['P c a n'], '-P 2n 2ab (y,-x,z)', 'y,-x,z'],
    [60, 0, ['P n c a'], '-P 2n 2ab (z,x,y)', 'z,x,y'],
    [60, 0, ['P n a b'], '-P 2n 2ab (z,y,-x)', 'z,y,-x'],
    [60, 0, ['P b n a'], '-P 2n 2ab (y,z,x)', 'y,z,x'],
    [60, 0, ['P c n b'], '-P 2n 2ab (-x,z,y)', '-x,z,y'],
    [61, 61, ['P b c a', 'P 21/b 21/c 21/a'], '-P 2ac 2ab', 'x,y,z'],
    [61, 0, ['P c a b'], '-P 2ac 2ab (z,y,-x)', 'z,y,-x'],
    [62, 62, ['P n m a', 'P 21/n 21/m 21/a'], '-P 2ac 2n', 'x,y,z'],
    [62, 0, ['P m n b'], '-P 2ac 2n (y,-x,z)', 'y,-x,z'],
    [62, 0, ['P b n m'], '-P 2ac 2n (z,x,y)', 'z,x,y'],
    [62, 0, ['P c m n'], '-P 2ac 2n (z,y,-x)', 'z,y,-x'],
    [62, 0, ['P m c n'], '-P 2ac 2n (y,z,x)', 'y,z,x'],
    [62, 0, ['P n a m'], '-P 2ac 2n (-x,z,y)', '-x,z,y'],
    [63, 63, ['C m c m', 'C 2/m 2/c 21/m'], '-C 2c 2', 'x,y,z'],
    [63, 0, ['C c m m'], '-C 2c 2 (y,-x,z)', 'y,-x,z'],
    [63, 0, ['A m m a'], '-C 2c 2 (z,x,y)', 'z,x,y'],
    [63, 0, ['A m a m'], '-C 2c 2 (z,y,-x)', 'z,y,-x'],
    [63, 0, ['B b m m'], '-C 2c 2 (y,z,x)', 'y,z,x'],
    [63, 0, ['B m m b'], '-C 2c 2 (-x,z,y)', '-x,z,y'],
    [64, 64, ['C m c a', 'C 2/m 2/c 21/a'], '-C 2ac 2', 'x,y,z'],
    [64, 0, ['C c m b'], '-C 2ac 2 (y,-x,z)', 'y,-x,z'],
    [64, 0, ['A b m a'], '-C 2ac 2 (z,x,y)', 'z,x,y'],
    [64, 0, ['A c a m'], '-C 2ac 2 (z,y,-x)', 'z,y,-x'],
    [64, 0, ['B b c m'], '-C 2ac 2 (y,z,x)', 'y,z,x'],
    [64, 0, ['B m a b'], '-C 2ac 2 (-x,z,y)', '-x,z,y'],
    [65, 65, ['C m m m', 'C 2/m 2/m 2/m'], '-C 2 2', 'x,y,z'],
    [65, 0, ['A m m m'], '-C 2 2 (z,x,y)', 'z,x,y'],
    [65, 0, ['B m m m'], '-C 2 2 (y,z,x)', 'y,z,x'],
    [66, 66, ['C c c m', 'C 2/c 2/c 2/m'], '-C 2 2c', 'x,y,z'],
    [66, 0, ['A m a a'], '-C 2 2c (z,x,y)', 'z,x,y'],
    [66, 0, ['B b m b'], '-C 2 2c (y,z,x)', 'y,z,x'],
    [67, 67, ['C m m a', 'C 2/m 2/m 2/a'], '-C 2a 2', 'x,y,z'],
    [67, 0, ['C m m b'], '-C 2a 2 (x+1/4,y+1/4,z)', 'x+1/4,y+1/4,z'],
    [67, 0, ['A b m m'], '-C 2a 2 (z,x,y)', 'z,x,y'],
    [67, 0, ['A c m m'], '-C 2a 2 (z,y,-x)', 'z,y,-x'],
    [67, 0, ['B m c m'], '-C 2a 2 (y,z,x)', 'y,z,x'],
    [67, 0, ['B m a m'], '-C 2a 2 (-x,z,y)', '-x,z,y'],
    [68, 68, ['C c c a', 'C 2/c 2/c 2/a'], 'C 2 2 -1ac', 'x-1/2,y-1/4,z+1/4'],
    [68, 0, ['C c c a'], '-C 2a 2ac', 'x,y,z'],
    [68, 0, ['C c c b'], '-C 2a 2ac (x-1/2,y-1/4,z+1/4)', 'x-1/2,y-1/4,z+1/4'],
    [68, 0, ['C c c b'], '-C 2a 2ac (x-1/4,y-1/4,z)', 'x-1/4,y-1/4,z'],
    [68, 0, ['A b a a'], '-C 2a 2ac (z+1/4,x-1/2,y-1/4)', 'z+1/4,x-1/2,y-1/4'],
    [68, 0, ['A b a a'], '-C 2a 2ac (z,x,y)', 'z,x,y'],
    [68, 0, ['A c a a'], '-C 2a 2ac (z+1/4,x-1/2,y-1/4)', 'z+1/4,x-1/2,y-1/4'],
    [68, 0, ['A c a a'], '-C 2a 2ac (z,y,-x)', 'z,y,-x'],
    [68, 0, ['B b c b'], '-C 2a 2ac (y-1/4,z+1/4,x-1/2)', 'y-1/4,z+1/4,x-1/2'],
    [68, 0, ['B b c b'], '-C 2a 2ac (y,z,x)', 'y,z,x'],
    [68, 0, ['B b a b'], '-C 2a 2ac (y-1/4,z+1/4,x-1/2)', 'y-1/4,z+1/4,x-1/2'],
    [68, 0, ['B b a b'], '-C 2a 2ac (-x,z,y)', '-x,z,y'],
    [69, 69, ['F m m m', 'F 2/m 2/m 2/m'], '-F 2 2', 'x,y,z'],
    [70, 70, ['F d d d', 'F 2/d 2/d 2/d'], 'F 2 2 -1d', 'x+1/8,y+1/8,z+1/8'],
    [70, 0, ['F d d d'], '-F 2uv 2vw', 'x,y,z'],
    [71, 71, ['I m m m', 'I 2/m 2/m 2/m'], '-I 2 2', 'x,y,z'],
    [72, 72, ['I b a m', 'I 2/b 2/a 2/m'], '-I 2 2c', 'x,y,z'],
    [72, 0, ['I m c b'], '-I 2 2c (z,x,y)', 'z,x,y'],
    [72, 0, ['I c m a'], '-I 2 2c (y,z,x)', 'y,z,x'],
    [73, 73, ['I b c a', 'I 21/b 21/c 21/a'], '-I 2b 2c', 'x,y,z'],
    [73, 0, ['I c a b'], '-I 2b 2c (x+1/4,y-1/4,z+1/4)', 'x+1/4,y-1/4,z+1/4'],
    [74, 74, ['I m m a', 'I 21/m 21/m 21/a'], '-I 2b 2', 'x,y,z'],
    [74, 0, ['I m m b'], '-I 2b 2 (x+1/4,y-1/4,z+1/4)', 'x+1/4,y-1/4,z+1/4'],
    [74, 0, ['I b m m'], '-I 2b 2 (z,x,y)', 'z,x,y'],
    [74, 0, ['I c m m'], '-I 2b 2 (z,y,-x)', 'z,y,-x'],
    [74, 0, ['I m c m'], '-I 2b 2 (y,z,x)', 'y,z,x'],
    [74, 0, ['I m a m'], '-I 2b 2 (-x,z,y)', '-x,z,y'],
    [75, 75, ['P 4'], 'P 4', 'x,y,z'],
    [76, 76, ['P 41'], 'P 4w', 'x,y,z'],
    [77, 77, ['P 42'], 'P 4c', 'x,y,z'],
    [78, 78, ['P 43'], 'P 4cw', 'x,y,z'],
    [79, 79, ['I 4'], 'I 4', 'x,y,z'],
    [80, 80, ['I 41'], 'I 4bw', 'x,y,z'],
    [81, 81, ['P -4'], 'P -4', 'x,y,z'],
    [82, 82, ['I -4'], 'I -4', 'x,y,z'],
    [83, 83, ['P 4/m'], '-P 4', 'x,y,z'],
    [84, 84, ['P 42/m'], '-P 4c', 'x,y,z'],
    [85, 85, ['P 4/n'], 'P 4ab -1ab', 'x-1/4,y+1/4,z'],
    [85, 0, ['P 4/n'], '-P 4a', 'x,y,z'],
    [86, 86, ['P 42/n'], 'P 4n -1n', 'x+1/4,y+1/4,z+1/4'],
    [86, 0, ['P 42/n'], '-P 4bc', 'x,y,z'],
    [87, 87, ['I 4/m'], '-I 4', 'x,y,z'],
    [88, 88, ['I 41/a'], 'I 4bw -1bw', 'x,y+1/4,z+1/8'],
    [88, 0, ['I 41/a'], '-I 4ad', 'x,y,z'],
    [89, 89, ['P 4 2 2'], 'P 4 2', 'x,y,z'],
    [90, 90, ['P 4 21 2'], 'P 4ab 2ab', 'x,y,z'],
    [91, 91, ['P 41 2 2'], 'P 4w 2c', 'x,y,z'],
    [92, 92, ['P 41 21 2'], 'P 4abw 2nw', 'x,y,z'],
    [93, 93, ['P 42 2 2'], 'P 4c 2', 'x,y,z'],
    [94, 94, ['P 42 21 2'], 'P 4n 2n', 'x,y,z'],
    [95, 95, ['P 43 2 2'], 'P 4cw 2c', 'x,y,z'],
    [96, 96, ['P 43 21 2'], 'P 4nw 2abw', 'x,y,z'],
    [97, 97, ['I 4 2 2'], 'I 4 2', 'x,y,z'],
    [98, 98, ['I 41 2 2'], 'I 4bw 2bw', 'x,y,z'],
    [99, 99, ['P 4 m m'], 'P 4 -2', 'x,y,z'],
    [100, 100, ['P 4 b m'], 'P 4 -2ab', 'x,y,z'],
    [101, 101, ['P 42 c m'], 'P 4c -2c', 'x,y,z'],
    [102, 102, ['P 42 n m'], 'P 4n -2n', 'x,y,z'],
    [103, 103, ['P 4 c c'], 'P 4 -2c', 'x,y,z'],
    [104, 104, ['P 4 n c'], 'P 4 -2n', 'x,y,z'],
    [105, 105, ['P 42 m c'], 'P 4c -2', 'x,y,z'],
    [106, 106, ['P 42 b c'], 'P 4c -2ab', 'x,y,z'],
    [107, 107, ['I 4 m m'], 'I 4 -2', 'x,y,z'],
    [108, 108, ['I 4 c m'], 'I 4 -2c', 'x,y,z'],
    [109, 109, ['I 41 m d'], 'I 4bw -2', 'x,y,z'],
    [110, 110, ['I 41 c d'], 'I 4bw -2c', 'x,y,z'],
    [111, 111, ['P -4 2 m'], 'P -4 2', 'x,y,z'],
    [112, 112, ['P -4 2 c'], 'P -4 2c', 'x,y,z'],
    [113, 113, ['P -4 21 m'], 'P -4 2ab', 'x,y,z'],
    [114, 114, ['P -4 21 c'], 'P -4 2n', 'x,y,z'],
    [115, 115, ['P -4 m 2'], 'P -4 -2', 'x,y,z'],
    [116, 116, ['P -4 c 2'], 'P -4 -2c', 'x,y,z'],
    [117, 117, ['P -4 b 2'], 'P -4 -2ab', 'x,y,z'],
    [118, 118, ['P -4 n 2'], 'P -4 -2n', 'x,y,z'],
    [119, 119, ['I -4 m 2'], 'I -4 -2', 'x,y,z'],
    [120, 120, ['I -4 c 2'], 'I -4 -2c', 'x,y,z'],
    [121, 121, ['I -4 2 m'], 'I -4 2', 'x,y,z'],
    [122, 122, ['I -4 2 d'], 'I -4 2bw', 'x,y,z'],
    [123, 123, ['P4/m m m', 'P 4/m 2/m 2/m'], '-P 4 2', 'x,y,z'],
    [124, 124, ['P4/m c c', 'P 4/m 2/c 2/c'], '-P 4 2c', 'x,y,z'],
    [125, 125, ['P4/n b m', 'P 4/n 2/b 2/m'], 'P 4 2 -1ab', 'x-1/4,y-1/4,z'],
    [125, 0, ['P 4/n b m'], '-P 4a 2b', 'x,y,z'],
    [126, 126, ['P4/n n c', 'P 4/n 2/n 2/c'], 'P 4 2 -1n', 'x-1/4,y-1/4,z-1/4'],
    [126, 0, ['P 4/n n c'], '-P 4a 2bc', 'x,y,z'],
    [127, 127, ['P4/m b m', 'P 4/m 21/b 2/m'], '-P 4 2ab', 'x,y,z'],
    [128, 128, ['P4/m n c', 'P 4/m 21/n 2/c'], '-P 4 2n', 'x,y,z'],
    [129, 129, ['P4/n m m', 'P 4/n 21/m 2/m'], 'P 4ab 2ab -1ab', 'x-1/4,y+1/4,z'],
    [129, 0, ['P 4/n m m'], '-P 4a 2a', 'x,y,z'],
    [130, 130, ['P4/n c c', 'P 4/n 2/c 2/c'], 'P 4ab 2n -1ab', 'x-1/4,y+1/4,z'],
    [130, 0, ['P 4/n c c'], '-P 4a 2ac', 'x,y,z'],
    [131, 131, ['P42/m m c', 'P 42/m 2/m 2/c'], '-P 4c 2', 'x,y,z'],
    [132, 132, ['P42/m c m', 'P 42/m 2/c 2/m'], '-P 4c 2c', 'x,y,z'],
    [133, 133, ['P42/n b c', 'P 42/n 2/b 2/c'], 'P 4n 2c -1n', 'x-1/4,y+1/4,z+1/4'],
    [133, 0, ['P 42/n b c'], '-P 4ac 2b', 'x,y,z'],
    [134, 134, ['P42/n n m', 'P 42/n 2/n 2/m'], 'P 4n 2 -1n', 'x-1/4,y+1/4,z-1/4'],
    [134, 0, ['P 42/n n m'], '-P 4ac 2bc', 'x,y,z'],
    [135, 135, ['P42/m b c', 'P 42/m 21/b 2/c'], '-P 4c 2ab', 'x,y,z'],
    [136, 136, ['P42/m n m', 'P 42/m 21/n 2/m'], '-P 4n 2n', 'x,y,z'],
    [137, 137, ['P42/n m c', 'P 42/n 21/m 2/c'], 'P 4n 2n -1n', 'x-1/4,y+1/4,z+1/4'],
    [137, 0, ['P 42/n m c'], '-P 4ac 2a', 'x,y,z'],
    [138, 138, ['P42/n c m', 'P 42/n 21/c 2/m'], 'P 4n 2ab -1n', 'x-1/4,y+1/4,z-1/4'],
    [138, 0, ['P 42/n c m'], '-P 4ac 2ac', 'x,y,z'],
    [139, 139, ['I4/m m m', 'I 4/m 2/m 2/m'], '-I 4 2', 'x,y,z'],
    [140, 140, ['I4/m c m', 'I 4/m 2/c 2/m'], '-I 4 2c', 'x,y,z'],
    [141, 141, ['I41/a m d', 'I 41/a 2/m 2/d'], 'I 4bw 2bw -1bw', 'x-1/2,y+1/4,z+1/8'],
    [141, 0, ['I 41/a m d'], '-I 4bd 2', 'x,y,z'],
    [142, 142, ['I41/a c d', 'I 41/a 2/c 2/d'], 'I 4bw 2aw -1bw', 'x-1/2,y+1/4,z-3/8'],
    [142, 0, ['I 41/a c d'], '-I 4bd 2c', 'x,y,z'],
    [143, 143, ['P 3'], 'P 3', 'x,y,z'],
    [144, 144, ['P 31'], 'P 31', 'x,y,z'],
    [145, 145, ['P 32'], 'P 32', 'x,y,z'],
    [146, 146, ['H 3'], 'R 3', 'x,y,z'],
    [146, 1146, ['R 3'], 'P 3*', '-y+z,x+z,-x+y+z'],
    [147, 147, ['P -3'], '-P 3', 'x,y,z'],
    [148, 148, ['H -3'], '-R 3', 'x,y,z'],
    [148, 1148, ['R -3'], '-P 3*', '-y+z,x+z,-x+y+z'],
    [149, 149, ['P 3 1 2'], 'P 3 2', 'x,y,z'],
    [150, 150, ['P 3 2 1'], 'P 3 2"', 'x,y,z'],
    [151, 151, ['P 31 1 2'], 'P 31 2 (0 0 4)', 'x,y,z'],
    [152, 152, ['P 31 2 1'], 'P 31 2"', 'x,y,z'],
    [153, 153, ['P 32 1 2'], 'P 32 2 (0 0 2)', 'x,y,z'],
    [154, 154, ['P 32 2 1'], 'P 32 2"', 'x,y,z'],
    [155, 155, ['H 3 2'], 'R 3 2"', 'x,y,z'],
    [155, 1155, ['R 3 2'], 'P 3* 2', '-y+z,x+z,-x+y+z'],
    [156, 156, ['P 3 m 1'], 'P 3 -2"', 'x,y,z'],
    [157, 157, ['P 3 1 m'], 'P 3 -2', 'x,y,z'],
    [158, 158, ['P 3 c 1'], 'P 3 -2"c', 'x,y,z'],
    [159, 159, ['P 3 1 c'], 'P 3 -2c', 'x,y,z'],
    [160, 160, ['H 3 m'], 'R 3 -2"', 'x,y,z'],
    [160, 1160, ['R 3 m'], 'P 3* -2', '-y+z,x+z,-x+y+z'],
    [161, 161, ['H 3 c'], 'R 3 -2"c', 'x,y,z'],
    [161, 1161, ['R 3 c'], 'P 3* -2n', '-y+z,x+z,-x+y+z'],
    [162, 162, ['P -3 1 m', 'P -3 1 2/m'], '-P 3 2', 'x,y,z'],
    [163, 163, ['P -3 1 c', 'P -3 1 2/c'], '-P 3 2c', 'x,y,z'],
    [164, 164, ['P -3 m 1', 'P -3 2/m 1'], '-P 3 2"', 'x,y,z'],
    [165, 165, ['P -3 c 1', 'P -3 2/c 1'], '-P 3 2"c', 'x,y,z'],
    [166, 166, ['H -3 m', 'H -3 2/m'], '-R 3 2"', 'x,y,z'],
    [166, 1166, ['R -3 m', 'R -3 2/m'], '-P 3* 2', '-y+z,x+z,-x+y+z'],
    [167, 167, ['H -3 c', 'H -3 2/c'], '-R 3 2"c', 'x,y,z'],
    [167, 1167, ['R -3 c', 'R -3 2/c'], '-P 3* 2n', '-y+z,x+z,-x+y+z'],
    [168, 168, ['P 6'], 'P 6', 'x,y,z'],
    [169, 169, ['P 61'], 'P 61', 'x,y,z'],
    [170, 170, ['P 65'], 'P 65', 'x,y,z'],
    [171, 171, ['P 62'], 'P 62', 'x,y,z'],
    [172, 172, ['P 64'], 'P 64', 'x,y,z'],
    [173, 173, ['P 63'], 'P 6c', 'x,y,z'],
    [174, 174, ['P -6'], 'P -6', 'x,y,z'],
    [175, 175, ['P 6/m'], '-P 6', 'x,y,z'],
    [176, 176, ['P 63/m'], '-P 6c', 'x,y,z'],
    [177, 177, ['P 6 2 2'], 'P 6 2', 'x,y,z'],
    [178, 178, ['P 61 2 2'], 'P 61 2 (0 0 5)', 'x,y,z'],
    [179, 179, ['P 65 2 2'], 'P 65 2 (0 0 1)', 'x,y,z'],
    [180, 180, ['P 62 2 2'], 'P 62 2 (0 0 4)', 'x,y,z'],
    [181, 181, ['P 64 2 2'], 'P 64 2 (0 0 2)', 'x,y,z'],
    [182, 182, ['P 63 2 2'], 'P 6c 2c', 'x,y,z'],
    [183, 183, ['P 6 m m'], 'P 6 -2', 'x,y,z'],
    [184, 184, ['P 6 c c'], 'P 6 -2c', 'x,y,z'],
    [185, 185, ['P 63 c m'], 'P 6c -2', 'x,y,z'],
    [186, 186, ['P 63 m c'], 'P 6c -2c', 'x,y,z'],
    [187, 187, ['P -6 m 2'], 'P -6 2', 'x,y,z'],
    [188, 188, ['P -6 c 2'], 'P -6c 2', 'x,y,z'],
    [189, 189, ['P -6 2 m'], 'P -6 -2', 'x,y,z'],
    [190, 190, ['P -6 2 c'], 'P -6c -2c', 'x,y,z'],
    [191, 191, ['P 6/m m m', 'P 6/m 2/m 2/m'], '-P 6 2', 'x,y,z'],
    [192, 192, ['P 6/m c c', 'P 6/m 2/c 2/c'], '-P 6 2c', 'x,y,z'],
    [193, 193, ['P 63/m c m', 'P 63/m 2/c 2/m'], '-P 6c 2', 'x,y,z'],
    [194, 194, ['P 63/m m c', 'P 63/m 2/m 2/c'], '-P 6c 2c', 'x,y,z'],
    [195, 195, ['P 2 3'], 'P 2 2 3', 'x,y,z'],
    [196, 196, ['F 2 3'], 'F 2 2 3', 'x,y,z'],
    [197, 197, ['I 2 3'], 'I 2 2 3', 'x,y,z'],
    [198, 198, ['P 21 3'], 'P 2ac 2ab 3', 'x,y,z'],
    [199, 199, ['I 21 3'], 'I 2b 2c 3', 'x,y,z'],
    [200, 200, ['P m -3', 'P 2/m -3'], '-P 2 2 3', 'x,y,z'],
    [201, 201, ['P n -3', 'P 2/n -3'], 'P 2 2 3 -1n', 'x-1/4,y-1/4,z-1/4'],
    [201, 0, ['P n -3'], '-P 2ab 2bc 3', 'x,y,z'],
    [202, 202, ['F m -3', 'F 2/m -3'], '-F 2 2 3', 'x,y,z'],
    [203, 203, ['F d -3', 'F 2/d -3'], 'F 2 2 3 -1d', 'x+1/8,y+1/8,z+1/8'],
    [203, 0, ['F d -3'], '-F 2uv 2vw 3', 'x,y,z'],
    [204, 204, ['I m -3', 'I 2/m -3'], '-I 2 2 3', 'x,y,z'],
    [205, 205, ['P a -3', 'P 21/a -3'], '-P 2ac 2ab 3', 'x,y,z'],
    [206, 206, ['I a -3', 'I 21/a -3'], '-I 2b 2c 3', 'x,y,z'],
    [207, 207, ['P 4 3 2'], 'P 4 2 3', 'x,y,z'],
    [208, 208, ['P 42 3 2'], 'P 4n 2 3', 'x,y,z'],
    [209, 209, ['F 4 3 2'], 'F 4 2 3', 'x,y,z'],
    [210, 210, ['F 41 3 2'], 'F 4d 2 3', 'x,y,z'],
    [211, 211, ['I 4 3 2'], 'I 4 2 3', 'x,y,z'],
    [212, 212, ['P 43 3 2'], 'P 4acd 2ab 3', 'x,y,z'],
    [213, 213, ['P 41 3 2'], 'P 4bd 2ab 3', 'x,y,z'],
    [214, 214, ['I 41 3 2'], 'I 4bd 2c 3', 'x,y,z'],
    [215, 215, ['P -4 3 m'], 'P -4 2 3', 'x,y,z'],
    [216, 216, ['F -4 3 m'], 'F -4 2 3', 'x,y,z'],
    [217, 217, ['I -4 3 m'], 'I -4 2 3', 'x,y,z'],
    [218, 218, ['P -4 3 n'], 'P -4n 2 3', 'x,y,z'],
    [219, 219, ['F -4 3 c'], 'F -4a 2 3', 'x,y,z'],
    [220, 220, ['I -4 3 d'], 'I -4bd 2c 3', 'x,y,z'],
    [221, 221, ['P m -3 m', 'P 4/m -3 2/m'], '-P 4 2 3', 'x,y,z'],
    [222, 222, ['P n -3 n', 'P 4/n -3 2/n'], 'P 4 2 3 -1n', 'x-1/4,y-1/4,z-1/4'],
    [222, 0, ['P n -3 n'], '-P 4a 2bc 3', 'x,y,z'],
    [223, 223, ['P m -3 n', 'P 42/m -3 2/n'], '-P 4n 2 3', 'x,y,z'],
    [224, 224, ['P n -3 m', 'P 42/n -3 2/m'], 'P 4n 2 3 -1n', 'x+1/4,y+1/4,z+1/4'],
    [224, 0, ['P n -3 m'], '-P 4bc 2bc 3', 'x,y,z'],
    [225, 225, ['F m -3 m', 'F 4/m -3 2/m'], '-F 4 2 3', 'x,y,z'],
    [226, 226, ['F m -3 c', 'F 4/m -3 2/c'], '-F 4a 2 3', 'x,y,z'],
    [227, 227, ['F d -3 m', 'F 41/d -3 2/m'], 'F 4d 2 3 -1d', 'x+1/8,y+1/8,z+1/8'],
    [227, 0, ['F d -3 m'], '-F 4vw 2vw 3', 'x,y,z'],
    [228, 228, ['F d -3 c', 'F 41/d -3 2/c'], 'F 4d 2 3 -1ad', 'x-1/8,y-1/8,z-1/8'],
    [228, 0, ['F d -3 c'], '-F 4ud 2vw 3', 'x,y,z'],
    [229, 229, ['I m -3 m', 'I 4/m -3 2/m'], '-I 4 2 3', 'x,y,z'],
    [230, 230, ['I a -3 d', 'I 41/a -3 2/d'], '-I 4bd 2c 3', 'x,y,z'],
    [5, 5005, ['I 1 21 1'], 'I 2yb', 'x+1/4,y+1/4,-x+z-1/4'],
    [5, 3005, ['C 1 21 1'], 'C 2yb', 'x+1/4,y+1/4,z'],
    [8, 1008, ['B 1 1 m'], 'B -2', '-x,z,y'],
    [18, 1018, ['P 21 21 2 (a)'], 'P 2ab 2a', 'x+1/4,y+1/4,z'],
    [20, 1020, ['C 2 2 21a)'], 'C 2ac 2', 'x+1/4,y,z'],
    [21, 1021, ['C 2 2 2a'], 'C 2ab 2b', 'x+1/4,y+1/4,z'],
    [22, 1022, ['F 2 2 2a'], 'F 2 2c', 'x,y,z+1/4'],
    [23, 1023, ['I 2 2 2a'], 'I 2ab 2bc', 'x-1/4,y+1/4,z-1/4'],
    [94, 1094, ['P 42 21 2a'], 'P 4bc 2a', 'x-1/4,y-1/4,z-1/4'],
    [197, 1197, ['I 2 3a'], 'I 2ab 2bc 3', 'x+1/4,y+1/4,z+1/4'],
];
